"use client";

import Link from "next/link";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  UsersIcon,
  OnlineIcon,
  ServerIcon,
  ArrowRightIcon,
  SearchIcon,
  CrownIcon,
  ShieldIcon,
} from "@/components/icons";

export default function JoinedOrganizationsPage() {
  const ownedOrgs = useQuery(api.organizations.getOwned);
  const joinedOrgsRaw = useQuery(api.organizations.getJoined);
  const organizations =
    ownedOrgs !== undefined && joinedOrgsRaw !== undefined
      ? joinedOrgsRaw.filter(
          (org) => !ownedOrgs.some((owned) => owned._id === org._id)
        )
      : undefined;

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Joined Organizations
          </h1>
          <p className="mt-1 text-muted">
            Communities you&apos;re a member of
          </p>
        </div>
        <Link href="/servers">
          <Button variant="outline">
            <SearchIcon className="mr-2 h-4 w-4" />
            Find More
          </Button>
        </Link>
      </div>

      {/* Organizations List */}
      {organizations === undefined ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      ) : organizations.length === 0 ? (
        <Card className="py-16">
          <CardContent className="text-center">
            <UsersIcon className="mx-auto h-12 w-12 text-muted" />
            <h2 className="mt-4 text-xl font-semibold text-foreground">
              No organizations joined
            </h2>
            <p className="mt-2 text-muted">
              Browse the Server Finder to discover communities
            </p>
            <Link href="/servers" className="mt-6 inline-block">
              <Button>
                <SearchIcon className="mr-2 h-4 w-4" />
                Browse Servers
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {organizations.map((org) => (
            <OrgCard key={org._id} org={org} />
          ))}
        </div>
      )}
    </div>
  );
}

function OrgCard({
  org,
}: {
  org: {
    _id: string;
    name: string;
    slug: string;
    description?: string;
    serverIp?: string;
    memberCount: number;
    onlineCount: number;
    memberRole: string;
  };
}) {
  const getRoleIcon = () => {
    switch (org.memberRole) {
      case "owner":
        return <CrownIcon className="h-3 w-3" />;
      case "admin":
      case "moderator":
        return <ShieldIcon className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const getRoleBadgeVariant = () => {
    switch (org.memberRole) {
      case "owner":
        return "default";
      case "admin":
        return "secondary";
      case "moderator":
        return "outline";
      default:
        return "outline";
    }
  };

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg">{org.name}</CardTitle>
          <Badge variant={getRoleBadgeVariant()} className="gap-1 capitalize">
            {getRoleIcon()}
            {org.memberRole}
          </Badge>
        </div>
        {org.description && (
          <p className="line-clamp-2 text-sm text-muted">{org.description}</p>
        )}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-between gap-4">
        {/* Stats */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1 text-muted">
            <UsersIcon className="h-4 w-4" />
            <span>{org.memberCount} members</span>
          </div>
          <div className="flex items-center gap-1 text-primary">
            <OnlineIcon className="h-4 w-4" />
            <span>{org.onlineCount} online</span>
          </div>
        </div>

        {/* Server IP */}
        {org.serverIp && (
          <div className="flex items-center gap-2 text-sm text-muted">
            <ServerIcon className="h-4 w-4" />
            <code>{org.serverIp}</code>
          </div>
        )}

        {/* Actions */}
        <Link href={`/org/${org.slug}`}>
          <Button variant="outline" className="w-full">
            Open
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
